import type { FeatureFlagKey } from '@/constants/types';
import { useCallback } from 'react';
import { useFeatureFlagsSelector } from './useFeatureFlagsSelector';

/**
 * Subscribes to a single feature flag value.
 *
 * This is the most ergonomic and safest way for UI components to read flags.
 * The component will only rerender when the specified flag changes.
 *
 * Internally this uses `useFeatureFlagsSelector` with a minimal selector to ensure
 * fine-grained subscription behavior.
 *
 * ✅ Use this hook when:
 * - a component depends on exactly one flag
 * - or the several flags it depends on don't relate to each other, if they do relate to each other they might be combinable via `useFeatureFlagsSelector`
 *
 * Example:
 * ```ts
 * const newCheckoutEnabled = useFeatureFlag('newCheckout');
 * ```
 *
 * @param key - The specific feature flag to subscribe to.
 * @returns The current value of the requested feature flag.
 */
export function useFeatureFlag(key: FeatureFlagKey) {
  return useFeatureFlagsSelector(useCallback((flags) => flags[key], [key]));
}
